/**
 * Email Marketing analytics — pure per-sequence rollups + smart prompts.
 *
 * The operator-facing answer to "does Email Marketing actually work?":
 *   1. {@link summarizeEmailAnalytics} folds REAL approval rows (governed
 *      send diffs tagged `sequence-enrollment …` / `campaign-send …`) plus
 *      any observed {@link EmailLogEvent}s into one honest row per
 *      sequence/campaign: queued vs sent vs vetoed, replies, and opens.
 *   2. {@link explainQueuedState} turns the pending/approved/dismissed
 *      counts of a batch into a plain-English sentence that says exactly
 *      what "queued" means and WHERE the diffs went (the Approvals inbox).
 *   3. {@link nextBestAction} picks the single most useful next step from
 *      the current surface state — a deterministic priority ladder, never
 *      a guess.
 *
 * Deliberately PURE: no store, no firebase, no clock, no fabrication.
 * Opens are only reported when an 'opened' event was actually observed —
 * until a Brevo webhook atom emits them, {@link EmailAnalyticsSummary}
 * honestly reports `openTrackingAvailable: false` and the UI shows "—",
 * never a fake zero-that-looks-like-data.
 */

import type { EmailLogEvent } from './sequenceAnalytics';

// ===========================================================================
// Send-note parsing — the shared tagging convention of the governed send path
// ===========================================================================

/** Where a governed send diff came from. */
export type SendChannelKind = 'sequence' | 'campaign';

/** The parsed identity of one governed send diff, read off its note. */
export interface ParsedSendNote {
  kind: SendChannelKind;
  /** Sequence id or campaign id. */
  id: string;
  /** Enrollment batch id (sequence sends only). */
  batchId?: string;
  /** Sequence step id (sequence sends only). */
  stepId?: string;
}

/**
 * Parse the note stamped onto every governed send approval:
 *   `sequence-enrollment <seqId> batch <batchId> step <stepId> …`
 *   `campaign-send <campaignId> to <recipient> …`
 * Anything else returns null — other approval rows are not email sends.
 */
export function parseSendNote(note: string | undefined): ParsedSendNote | null {
  if (!note) return null;
  const seq = note.match(/^sequence-enrollment (\S+) batch (\S+)(?: step (\S+))?/);
  if (seq) {
    return {
      kind: 'sequence',
      id: seq[1],
      batchId: seq[2],
      ...(seq[3] !== undefined ? { stepId: seq[3] } : {}),
    };
  }
  const camp = note.match(/^campaign-send (\S+)/);
  if (camp) return { kind: 'campaign', id: camp[1] };
  return null;
}

// ===========================================================================
// Per-sequence rollup
// ===========================================================================

/** Minimal structural subset of ApprovalItem the summarizer needs. */
export interface SendApprovalRow {
  status: 'pending' | 'approved' | 'dismissed';
  note?: string;
}

/** One honest row per sequence/campaign in the analytics table. */
export interface SequenceSendSummary {
  id: string;
  kind: SendChannelKind;
  /** Resolved display name; falls back to the raw id. */
  name: string;
  /** Send diffs still waiting for a human in the Approvals inbox. */
  queued: number;
  /** Diffs a human approved — executed down the governed send path. */
  sent: number;
  /** Diffs a human dismissed — vetoed, never executed. */
  vetoed: number;
  /** Distinct enrollment batches observed (campaigns count as 1). */
  batches: number;
  /** Observed replies attributed to this sequence. */
  replies: number;
  /** Observed opens — only meaningful when open tracking is available. */
  opens: number;
}

export interface EmailAnalyticsSummary {
  perSequence: SequenceSendSummary[];
  totals: {
    queued: number;
    sent: number;
    vetoed: number;
    replies: number;
    opens: number;
  };
  /**
   * True iff at least one 'opened' event was actually observed anywhere.
   * When false, no open-tracking source exists (Brevo webhook atom not
   * wired) and opens MUST render as "—", not a fake 0.
   */
  openTrackingAvailable: boolean;
}

/**
 * Fold approval rows + email-log events into per-sequence summaries.
 * Pure and total: rows whose notes are not send diffs are ignored, events
 * without a sequenceId are ignored (nothing to attribute them to). Rows
 * sort by queued desc, then sent desc, then id — the most actionable
 * sequence is always first.
 */
export function summarizeEmailAnalytics(
  approvals: SendApprovalRow[],
  events: EmailLogEvent[] = [],
  names: Record<string, string> = {},
): EmailAnalyticsSummary {
  const rows = new Map<string, SequenceSendSummary & { batchIds: Set<string> }>();

  const rowFor = (id: string, kind: SendChannelKind) => {
    let row = rows.get(id);
    if (!row) {
      row = {
        id,
        kind,
        name: names[id] ?? id,
        queued: 0,
        sent: 0,
        vetoed: 0,
        batches: 0,
        replies: 0,
        opens: 0,
        batchIds: new Set<string>(),
      };
      rows.set(id, row);
    }
    return row;
  };

  for (const a of approvals) {
    const parsed = parseSendNote(a.note);
    if (!parsed) continue;
    const row = rowFor(parsed.id, parsed.kind);
    if (a.status === 'pending') row.queued++;
    else if (a.status === 'approved') row.sent++;
    else row.vetoed++;
    if (parsed.batchId !== undefined) row.batchIds.add(parsed.batchId);
  }

  let openTrackingAvailable = false;
  for (const e of events) {
    if (e.kind === 'opened') openTrackingAvailable = true;
    if (!e.sequenceId) continue;
    const row = rows.get(e.sequenceId);
    if (!row) continue; // never invent a sequence the send path didn't record
    if (e.kind === 'replied') row.replies++;
    if (e.kind === 'opened') row.opens++;
  }

  const perSequence = [...rows.values()]
    .map(({ batchIds, ...row }) => ({
      ...row,
      batches: row.kind === 'campaign' ? 1 : batchIds.size,
    }))
    .sort((a, b) => {
      if (a.queued !== b.queued) return b.queued - a.queued;
      if (a.sent !== b.sent) return b.sent - a.sent;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });

  const totals = { queued: 0, sent: 0, vetoed: 0, replies: 0, opens: 0 };
  for (const r of perSequence) {
    totals.queued += r.queued;
    totals.sent += r.sent;
    totals.vetoed += r.vetoed;
    totals.replies += r.replies;
    totals.opens += r.opens;
  }

  return { perSequence, totals, openTrackingAvailable };
}

// ===========================================================================
// Queued-state explainer — what "queued" means + where the diffs went
// ===========================================================================

/**
 * One plain-English sentence for a batch's live queue state. Honest by
 * construction: it only speaks about counts the caller actually observed.
 */
export function explainQueuedState(counts: {
  pending: number;
  approved: number;
  dismissed?: number;
}): string {
  const { pending, approved } = counts;
  const dismissed = counts.dismissed ?? 0;
  const parts: string[] = [];

  if (pending === 0 && approved === 0 && dismissed === 0) {
    return (
      'Nothing queued yet — when you submit, each recipient becomes one ' +
      'approvable send diff in the Approvals inbox on the Dashboard.'
    );
  }
  if (pending > 0) {
    parts.push(
      `${pending} send diff${pending === 1 ? ' is' : 's are'} queued — waiting in the ` +
        'Approvals inbox on the Dashboard. Approving a diff executes its governed ' +
        'send; dismissing vetoes it. Nothing sends on its own.',
    );
  }
  if (approved > 0) {
    parts.push(
      `${approved} approved — executed down the governed send path and sealed in the ledger.`,
    );
  }
  if (dismissed > 0) {
    parts.push(`${dismissed} dismissed — vetoed by a human, never executed.`);
  }
  return parts.join(' ');
}

// ===========================================================================
// Next best action — one deterministic smart prompt
// ===========================================================================

/** Everything the prompt ladder needs to know about the surface. */
export interface EmailMarketingSnapshot {
  /** Saved sequences available to enroll into. */
  sequences: number;
  /** Contacts in the selected cohort that actually have an email address. */
  cohortEmailable: number;
  /**
   * Enrolled count of the CURRENT previewed plan; null when no preview has
   * been built (or the last one was already submitted).
   */
  planEnrolled: number | null;
  /** Send diffs pending in the Approvals inbox (all sequences/campaigns). */
  queued: number;
  /** Send diffs already approved (governed sends executed). */
  sent: number;
  /** Active sequence-health warnings (bounce spike, low delivery, …). */
  healthWarnings: number;
}

export type NextBestActionId =
  | 'fix_health'
  | 'approve_queued'
  | 'create_sequence'
  | 'add_contacts'
  | 'submit_plan'
  | 'preview_plan'
  | 'review_analytics';

export interface NextBestAction {
  id: NextBestActionId;
  label: string;
  detail: string;
}

/**
 * The priority ladder, top first:
 *   1. health warnings — a bounce spike outranks everything (reputation).
 *   2. queued diffs — work is sitting in the Approvals inbox unseen.
 *   3. no sequences — nothing can send without a cadence; start a recipe.
 *   4. no emailable contacts — the cohort is empty; fix the audience.
 *   5. previewed plan pending — the blast radius is vetted; submit it.
 *   6. nothing sent yet — preview a blast radius to get moving.
 *   7. steady state — watch analytics; replies attribute automatically.
 */
export function nextBestAction(s: EmailMarketingSnapshot): NextBestAction {
  if (s.healthWarnings > 0) {
    return {
      id: 'fix_health',
      label: 'Review sequence health warnings',
      detail:
        `${s.healthWarnings} active warning${s.healthWarnings === 1 ? '' : 's'} in Sequence ` +
        'Performance below — a bounce spike burns mailbox reputation; fix it before queuing more sends.',
    };
  }
  if (s.queued > 0) {
    return {
      id: 'approve_queued',
      label: `Approve ${s.queued} queued send${s.queued === 1 ? '' : 's'}`,
      detail:
        'Send diffs are waiting in the Approvals inbox on the Dashboard — nothing sends until ' +
        'a human approves each one.',
    };
  }
  if (s.sequences === 0) {
    return {
      id: 'create_sequence',
      label: 'Create your first sequence',
      detail:
        'Start with a recipe above (one click) or the starter cadence — a sequence is required ' +
        'before anyone can be enrolled.',
    };
  }
  if (s.cohortEmailable === 0) {
    return {
      id: 'add_contacts',
      label: 'Pick a cohort with email addresses',
      detail:
        'The selected cohort has no contacts with an email address — switch cohorts or add ' +
        'emails in the CRM. Addresses are never guessed.',
    };
  }
  if (s.planEnrolled !== null && s.planEnrolled > 0) {
    return {
      id: 'submit_plan',
      label: `Submit the previewed batch (${s.planEnrolled} enrolled)`,
      detail:
        'The blast radius is vetted — submitting queues one approvable send diff per contact ' +
        'into the Approvals inbox.',
    };
  }
  if (s.sent === 0) {
    return {
      id: 'preview_plan',
      label: 'Preview a blast radius',
      detail:
        'Pick a cohort + sequence in Governed enrollment and preview — you see exactly who ' +
        'enrolls and who is suppressed (and why) before anything is queued.',
    };
  }
  return {
    id: 'review_analytics',
    label: 'Watch the analytics',
    detail:
      'Sends are flowing — replies attribute to steps automatically and the reply autopilot ' +
      'stops cadences the moment someone answers.',
  };
}
